/**
 * Altas desde Saber — GEN-EKB-020. Dos modales sobre la rejilla de «Lo que sabemos»:
 * «+ KB» (reutiliza POST /v1/ekb/kbs, solo administrador) y «+ Añadir saber» (reutiliza POST /v1/ekb/ikus de la 009,
 * versión 1). Sin mockup: tarjeta y botones del libro de estilo (§2). El front solo pinta lo que el kernel
 * dice (`puede_editar` por KB en el mapa); la puerta real es la revalidación del kernel en cada POST.
 */
import { useEffect, useState } from 'react'
import { crearIku, crearKb, editarKb, getAreas, listIkus, listKbs } from '../api/client.js'

const MENSAJES = {
  nombre_invalido: 'El nombre es obligatorio.',
  nombre_duplicado: 'Ya hay un KB activo con ese nombre.',
  titular_invalido: 'El titular solo puede ser el administrador o un champion.',
  area_retirada: 'El área está retirada: elige otra.',
  area_no_encontrada: 'El área ya no existe: elige otra.',
  etiqueta_no_confidencial: 'Un texto Confidencial solo puede ir a un KB Confidencial.',
  kb_no_encontrado: 'No tienes acceso a ese KB o ya no existe.',
  kb_retirado: 'El KB está retirado.',
  kb_con_ikus: 'Este KB tiene iKus que solo están en él: retira antes esos iKus.',
  forbidden: 'Tu perfil no puede hacer esto.',
}
export const mensaje = err => MENSAJES[err?.code] || err?.message || 'No se ha podido completar la operación.'
const ETIQUETAS = [['interna', 'Interna'], ['confidencial', 'Confidencial'], ['publica', 'Pública']]

// Áreas en orden de árbol con su profundidad (la subárea va indentada bajo su padre).
export function aplanarAreas(areas) {
  const hijos = new Map()
  for (const a of areas) hijos.set(a.padre_id ?? null, [...(hijos.get(a.padre_id ?? null) ?? []), a])
  const ids = new Set(areas.map(a => a.id))
  const salida = []
  const bajar = (padre, nivel) => (hijos.get(padre) ?? []).forEach(a => { salida.push({ ...a, nivel }); bajar(a.id, nivel + 1) })
  bajar(null, 0)
  areas.filter(a => a.padre_id && !ids.has(a.padre_id)).forEach(a => { salida.push({ ...a, nivel: 0 }); bajar(a.id, 1) })
  return salida
}

const MAX_TITULO = 80

// Título propuesto: la primera frase (o línea) del texto, recortada en palabra entera.
export function proponerTitulo(texto) {
  const t = (texto ?? '').trim()
  if (!t) return ''
  const frase = t.split(/\n|(?<=[.!?])\s/)[0].trim().replace(/[.!?:;,\s]+$/, '')
  if (frase.length <= MAX_TITULO) return frase
  const corte = frase.slice(0, MAX_TITULO)
  const palabra = corte.slice(0, corte.lastIndexOf(' ')).trim()
  return `${(palabra.length >= MAX_TITULO / 2 ? palabra : corte).replace(/[.,;:\s]+$/, '')}…`
}

// `rotulo`: qué se está tocando (Área, KB o iKu), pequeño y en mayúsculas sobre el título.
export function Modal({ titulo, rotulo, onCerrar, children }) {
  useEffect(() => {
    const alTeclear = e => { if (e.key === 'Escape') onCerrar() }
    document.addEventListener('keydown', alTeclear)
    return () => document.removeEventListener('keydown', alTeclear)
  }, [onCerrar])
  return (
    <div className="dk-modal" onMouseDown={e => { if (e.target === e.currentTarget) onCerrar() }}>
      <div className="dk-modal__tarjeta" role="dialog" aria-modal="true" aria-label={rotulo ?? titulo}>
        {rotulo && <div className="dk-rotulo">{rotulo}</div>}
        <h3 className="dk-modal__titulo">{titulo}</h3>
        {children}
      </div>
    </div>
  )
}

export function Acciones({ guardando, etiqueta, onCerrar, deshabilitado }) {
  return (
    <div className="dk-modal__acciones">
      <button type="button" className="dk-btn" onClick={onCerrar} disabled={guardando}>Cancelar</button>
      <button type="submit" className="dk-btn dk-btn--primario" disabled={guardando || deshabilitado}>{etiqueta}</button>
    </div>
  )
}

// `areaInicial` (GEN-PX-020e): área preseleccionada cuando se abre desde la tarjeta de un área.
export function ModalKb({ onCerrar, onCreado, areaInicial = null }) {
  const [areas, setAreas] = useState(null)
  const [titulares, setTitulares] = useState([])
  const [nombre, setNombre] = useState('')
  const [areaId, setAreaId] = useState('')
  const [etiqueta, setEtiqueta] = useState('interna')
  const [titular, setTitular] = useState('')
  const [error, setError] = useState(null)
  const [guardando, setGuardando] = useState(false)

  useEffect(() => {
    let vivo = true
    Promise.all([getAreas(), listKbs('activo')])
      .then(([a, k]) => {
        if (!vivo) return
        const lista = aplanarAreas(a.areas ?? [])
        setAreas(lista)
        setAreaId(lista.some(x => x.id === areaInicial) ? areaInicial : (lista[0]?.id ?? ''))
        setTitulares(k.titulares_posibles ?? [])
      })
      .catch(err => vivo && setError(mensaje(err)))
    return () => { vivo = false }
  }, [])

  async function enviar(e) {
    e.preventDefault()
    setGuardando(true)
    setError(null)
    try {
      const { kb } = await crearKb({ nombre: nombre.trim(), descripcion: '', titular, etiqueta, area_id: areaId || null })
      await onCreado(kb)
    } catch (err) {
      setError(mensaje(err))
      setGuardando(false)
    }
  }

  return (
    <Modal titulo="Nuevo KB" rotulo="KB · Bloque de conocimiento" onCerrar={onCerrar}>
      <form className="dk-modal__form" onSubmit={enviar}>
        <label>Nombre <input required maxLength={120} autoComplete="off" value={nombre} onChange={e => setNombre(e.target.value)} /></label>
        <label>
          Área
          <select required value={areaId} onChange={e => setAreaId(e.target.value)}>
            {(areas ?? []).map(a => <option key={a.id} value={a.id}>{'  '.repeat(a.nivel)}{a.icono} {a.nombre}</option>)}
          </select>
        </label>
        <label>
          Etiqueta
          <select value={etiqueta} onChange={e => setEtiqueta(e.target.value)}>
            {ETIQUETAS.map(([v, t]) => <option key={v} value={v}>{t}</option>)}
          </select>
        </label>
        <label>
          Titular
          <select value={titular} onChange={e => setTitular(e.target.value)}>
            <option value="">Yo (administrador)</option>
            {titulares.map(t => <option key={t.email} value={t.email}>{t.display_name} · {t.rol === 'champion' ? 'champion' : 'administrador'}</option>)}
          </select>
        </label>
        {error && <p className="dk-modal__error" role="alert">{error}</p>}
        <Acciones guardando={guardando} etiqueta="Crear KB" onCerrar={onCerrar} deshabilitado={!areas} />
      </form>
    </Modal>
  )
}

// `kbs`: los KBs del mapa con `puede_editar` (ya con el área en `area`); solo esos son destino.
export function ModalSaber({ kbs, onCerrar, onCreado }) {
  const editables = kbs.filter(k => k.puede_editar)
  const [texto, setTexto] = useState('')
  const [titulo, setTitulo] = useState('')
  const [tituloEditado, setTituloEditado] = useState(false)
  const [tipo, setTipo] = useState('hecho')
  const [tipos, setTipos] = useState([])
  const [kbId, setKbId] = useState('')
  const [vigencia, setVigencia] = useState('')
  const [confidencial, setConfidencial] = useState(false)
  const [error, setError] = useState(null)
  const [guardando, setGuardando] = useState(false)

  // Con «Confidencial» marcado solo valen los KBs Confidenciales (el kernel lo revalida con 409).
  const destinos = confidencial ? editables.filter(k => k.etiqueta === 'confidencial') : editables
  const destino = destinos.find(k => k.id === kbId) ?? null

  useEffect(() => {
    if (!destinos.some(k => k.id === kbId)) setKbId('')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [confidencial])

  useEffect(() => {
    let vivo = true
    // La lista de tipos es la de la 009: la sirve el kernel con cualquier KB.
    if (editables[0]) listIkus(editables[0].id, 'vigente').then(r => vivo && setTipos((r.tipos ?? []).filter(t => t.disponible))).catch(() => {})
    return () => { vivo = false }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  function cambiarTexto(v) {
    setTexto(v)
    if (!tituloEditado) setTitulo(proponerTitulo(v))
  }

  async function enviar(e) {
    e.preventDefault()
    if (!destino) { setError('Elige el KB de destino.'); return }
    setGuardando(true)
    setError(null)
    try {
      const { iku } = await crearIku({ kb_id: destino.id, titulo: titulo.trim(), resumen: '', tipo, vigencia, texto: texto.trim(), confidencial })
      await onCreado(iku, destino)
    } catch (err) {
      setError(mensaje(err))
      setGuardando(false)
    }
  }

  return (
    <Modal titulo="Añadir saber" rotulo="iKu · Unidad incremental de conocimiento" onCerrar={onCerrar}>
      <form className="dk-modal__form" onSubmit={enviar}>
        <label>Texto <textarea required rows={6} placeholder="Pega o escribe lo que la empresa debe saber" value={texto} onChange={e => cambiarTexto(e.target.value)} /></label>
        <label>Título <input required maxLength={200} autoComplete="off" value={titulo} onChange={e => { setTitulo(e.target.value); setTituloEditado(true) }} /></label>
        <label>
          Tipo
          <select value={tipo} onChange={e => setTipo(e.target.value)}>
            {(tipos.length ? tipos : [{ id: 'hecho', etiqueta: 'Hecho' }]).map(t => <option key={t.id} value={t.id}>{t.etiqueta}</option>)}
          </select>
        </label>
        <label className="dk-modal__check">
          <input type="checkbox" checked={confidencial} onChange={e => setConfidencial(e.target.checked)} /> Este texto es Confidencial
        </label>
        <label>
          KB de destino
          <select required value={kbId} onChange={e => setKbId(e.target.value)}>
            <option value="">Elige un KB…</option>
            {destinos.map(k => <option key={k.id} value={k.id}>{k.nombre}{k.area ? ` · ${k.area}` : ''}</option>)}
          </select>
        </label>
        <label>Vigencia (opcional) <input type="date" value={vigencia} onChange={e => setVigencia(e.target.value)} /></label>
        {confidencial && destinos.length === 0 && <p className="dk-modal__error" role="status">No tienes ningún KB Confidencial donde añadir.</p>}
        {error && <p className="dk-modal__error" role="alert">{error}</p>}
        <Acciones guardando={guardando} etiqueta="Añadir saber" onCerrar={onCerrar} />
      </form>
    </Modal>
  )
}

// GEN-PX-019: editar nombre y descripción de un KB que puedo gestionar (PUT /v1/ekb/kbs/{id}, la de «Mi empresa › KBs»).
export function ModalEditarKb({ kb, onCerrar, onGuardado }) {
  const [nombre, setNombre] = useState(kb.nombre ?? '')
  const [descripcion, setDescripcion] = useState(kb.descripcion ?? '')
  const [error, setError] = useState(null)
  const [guardando, setGuardando] = useState(false)

  async function enviar(e) {
    e.preventDefault()
    setGuardando(true)
    setError(null)
    try {
      await editarKb(kb.id, { nombre: nombre.trim(), descripcion: descripcion.trim() })
      await onGuardado({ ...kb, nombre: nombre.trim(), descripcion: descripcion.trim() })
    } catch (err) {
      setError(mensaje(err))
      setGuardando(false)
    }
  }

  return (
    <Modal titulo={kb.nombre || 'Editar KB'} rotulo="Editar KB · Bloque de conocimiento" onCerrar={onCerrar}>
      <form className="dk-modal__form" onSubmit={enviar}>
        <label>Nombre <input required maxLength={120} autoComplete="off" value={nombre} onChange={e => setNombre(e.target.value)} /></label>
        <label>Descripción <textarea rows={3} maxLength={500} value={descripcion} onChange={e => setDescripcion(e.target.value)} /></label>
        {error && <p className="dk-modal__error" role="alert">{error}</p>}
        <Acciones guardando={guardando} etiqueta="Guardar" onCerrar={onCerrar} />
      </form>
    </Modal>
  )
}
